import React, { useState, useEffect } from 'react';
import Login from './Login';
import CashDrawerModal from './CashDrawerModal';
import {
  clearLockedSession,
  getLockedSession,
  migrateLegacyLockedSession,
} from "../utils/lockedSessions";
import {
  apiFetch,
  isDefinitiveAuthenticationRejection,
} from "../utils/apiClient";

const API_BASE = "/api";

const CashierLogin = ({ onLogin, forceSuperadmin = false, requiredRole = null }) => {
  const [showCashDrawer, setShowCashDrawer] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [authenticatedToken, setAuthenticatedToken] = useState(null);
  const [lastAmount, setLastAmount] = useState(0);
  const [loading, setLoading] = useState(false);

  const resolveAuthToken = (tokenOverride = null) => {
    if (tokenOverride && String(tokenOverride).trim()) {
      return String(tokenOverride).trim();
    }
    const fromState = String(authenticatedToken || "").trim();
    if (fromState) return fromState;
    const fromStorage = String(localStorage.getItem("moo_token") || "").trim();
    return fromStorage || null;
  };

  const buildAuthHeaders = (tokenOverride = null) => {
    const resolved = resolveAuthToken(tokenOverride);
    return resolved ? { Authorization: `Bearer ${resolved}` } : {};
  };

  const resolveUserId = (user) => {
    const rawId = user?.id ?? user?.user_id ?? user?.userId ?? user?.cashier_id;
    const numericId = Number(rawId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  };

  const normalizeRole = (role) =>
    String(role || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isCashierRole = (role) => {
    const normalized = normalizeRole(role);
    return (
      normalized === "cashier" ||
      normalized === "caissier" ||
      normalized === "caisse" ||
      normalized.includes("cash") ||
      normalized.includes("caiss")
    );
  };

  const resetExpiredAuthentication = (rejectedToken = null) => {
    const latestToken = String(localStorage.getItem("moo_token") || "").trim();
    if (
      rejectedToken &&
      latestToken &&
      String(rejectedToken).trim() !== latestToken
    ) {
      // Une nouvelle connexion a remplacé le jeton pendant la vérification.
      // Ne jamais supprimer cette session plus récente.
      setAuthenticatedToken(latestToken);
      return false;
    }
    localStorage.removeItem("moo_token");
    localStorage.removeItem("moo_user");
    localStorage.removeItem("moo_cash_session");
    setAuthenticatedToken(null);
    setAuthenticatedUser(null);
    setShowCashDrawer(false);
    window.dispatchEvent(new Event("moo-auth-refresh"));
    return true;
  };

  const createAuthenticationExpiredError = () => {
    const error = new Error(
      "Votre connexion a expiré. Reconnectez-vous avec votre code PIN.",
    );
    error.code = "AUTH_SESSION_EXPIRED";
    return error;
  };

  const isAuthenticationStillValid = async (tokenOverride = null) => {
    const token = resolveAuthToken(tokenOverride);
    if (!token) return false;
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        cache: "no-store",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (isDefinitiveAuthenticationRejection(response.status)) return false;
      // Un 5xx ou une réponse intermédiaire ne prouve pas que le jeton est
      // invalide. Les routes métier restent l'autorité et refuseront toute
      // écriture réellement non authentifiée.
      return true;
    } catch (_error) {
      // Une panne réseau n'est pas une preuve d'expiration du jeton.
      return true;
    }
  };


  // Détecter si c'est un déverrouillage de session au chargement
  useEffect(() => {
    migrateLegacyLockedSession();
  }, []);

  // Fonction appelée après connexion PIN réussie
  const handlePinLogin = async (user, token) => {
    // En mode superadmin, on bypass toute logique caisse
    if (forceSuperadmin) {
      onLogin(user, token);
      return;
    }
    const userId = resolveUserId(user);
    const lockedSession = getLockedSession(userId);
    const isManualUnlock = lockedSession?.lockReason === "manual";
    // Si c'est un déverrouillage, on vérifie qu'une session de caisse active existe vraiment.
    // Sinon on considère le lock comme obsolète et on reprend un flux normal (modal fond de caisse).
    if (isManualUnlock && userId) {
      const unlockedSession = await checkExistingSession(userId, token);
      if (unlockedSession) {
        console.log('🔓 Déverrouillage - connexion directe sans modal de caisse');
        onLogin(user, token);
        return;
      }
      clearLockedSession(userId);
    }

    const shouldShowCashDrawer = isCashierRole(user?.role);

    // Pour caissier (hors déverrouillage), afficher le modal de fond de caisse
    if (shouldShowCashDrawer) {
      setAuthenticatedUser(user);
      setAuthenticatedToken(token || localStorage.getItem("moo_token"));
      const existingSession = await checkExistingSession(userId, token, {
        includeStale: true,
      });
      if (existingSession) {
        // Une saisie du PIN est une nouvelle connexion métier, jamais une
        // reprise silencieuse de la caisse précédente. Seul le déverrouillage
        // manuel, traité plus haut, conserve volontairement la même session.
        const closedAmount = await closeExistingSessionBeforeNewOne(
          existingSession,
          token,
        );
        localStorage.removeItem("moo_cash_session");
        if (Number.isFinite(closedAmount)) {
          setLastAmount(Math.max(0, Math.round(closedAmount)));
        } else {
          throw new Error(
            "L’ancienne session de caisse n’a pas pu être fermée. Réessayez.",
          );
        }
      } else {
        localStorage.removeItem("moo_cash_session");
        await fetchLastAmount(userId, token);
      }
      setShowCashDrawer(true);
    } else {
      // Pour les autres rôles, connexion directe
      onLogin(user, token);
    }
  };

  // Récupérer le montant de la dernière session fermée
  const fetchLastAmount = async (cashierId, tokenOverride = null) => {
    if (!cashierId) {
      setLastAmount(0);
      return;
    }
    try {
      const response = await apiFetch(`${API_BASE}/cash-sessions/last-amount/${cashierId}`, {
        headers: buildAuthHeaders(tokenOverride),
        cache: "no-store",
      });
      if (response.ok) {
        const data = await response.json();
        setLastAmount(data.lastAmount);
      }
    } catch (error) {
      console.error('Erreur récupération dernier montant:', error);
      setLastAmount(0);
    }
  };

  // Vérifier s'il y a déjà une session active
  const checkExistingSession = async (
    cashierId,
    tokenOverride = null,
    options = {},
  ) => {
    if (!cashierId) return null;
    try {
      const query = options.includeStale ? "?includeStale=1" : "";
      const response = await apiFetch(
        `${API_BASE}/cash-sessions/active${query}`,
        {
          headers: buildAuthHeaders(tokenOverride),
          cache: "no-store",
        },
      );
      if (response.ok) {
        const session = await response.json();
        const sessionCashierId = Number(session?.cashier_id);
        if (!Number.isFinite(sessionCashierId) || sessionCashierId !== Number(cashierId)) {
          return null;
        }
        // Si une session existe déjà, on procède à la connexion directe
        console.log('Session active trouvée:', session);
        return session;
      }
    } catch (error) {
      // Pas de session active (404 attendu)
      console.log('Pas de session active, création nécessaire');
    }
    return null;
  };
  const getSessionTheoreticalAmount = async (session, tokenOverride = null) => {
    if (!session?.id) return Number(session?.initial_amount || 0);
    try {
      const cashierId = Number(session?.cashier_id || resolveUserId(authenticatedUser));
      const cashierQuery = cashierId ? `?cashierId=${cashierId}` : "";
      const response = await apiFetch(
        `${API_BASE}/cash-journal/summary/${session.id}${cashierQuery}`,
        {
          headers: buildAuthHeaders(tokenOverride),
          cache: "no-store",
        },
      );
      if (response.ok) {
        const data = await response.json();
        const theoretical = Number(data?.summary?.theoretical_balance);
        if (Number.isFinite(theoretical)) {
          return Math.max(0, Math.round(theoretical));
        }
      }
    } catch (_error) {}
    return Number(session?.initial_amount || 0);
  };

  const closeExistingSessionBeforeNewOne = async (session, tokenOverride = null) => {
    if (!session?.id) return null;
    const finalAmount = await getSessionTheoreticalAmount(session, tokenOverride);
    const response = await apiFetch(`${API_BASE}/cash-sessions/${session.id}/close`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeaders(tokenOverride),
      },
      includeBearer: !resolveAuthToken(tokenOverride),
      tokenMode: forceSuperadmin ? "superadmin" : "admin",
      body: JSON.stringify({
        finalAmount,
        notes: "Session fermée avant une nouvelle connexion caisse",
      }),
    });
    if (!response.ok) return null;
    return finalAmount;
  };


  // Créer une nouvelle session de caisse
  const createCashSession = async (initialAmount, notes) => {
    setLoading(true);

    try {
      const authenticatedUserId = resolveUserId(authenticatedUser);
      if (!authenticatedUserId) {
        throw new Error("Identifiant caissier invalide");
      }
      // Vérifier d'abord s'il n'y a pas déjà une session active
      const authToken = resolveAuthToken();
      const normalizedInitialAmount = Number(initialAmount);
      const safeInitialAmount =
        Number.isFinite(normalizedInitialAmount) && normalizedInitialAmount >= 0
          ? normalizedInitialAmount
          : 0;

      const syncSessionInitialAmount = async (session) => {
        if (!session?.id) return session;
        const currentInitialAmount = Number(session?.initial_amount || 0);
        const hasDifference =
          Number.isFinite(currentInitialAmount) &&
          Math.abs(currentInitialAmount - safeInitialAmount) > 0.001;

        if (!hasDifference) {
          return session;
        }

        const updateResponse = await apiFetch(
          `${API_BASE}/cash-sessions/${session.id}/initial-amount`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...buildAuthHeaders(authToken),
            },
            includeBearer: !authToken,
            tokenMode: forceSuperadmin ? "superadmin" : "admin",
            body: JSON.stringify({ initialAmount: safeInitialAmount }),
          },
        );

        if (updateResponse.ok) {
          return updateResponse.json();
        }

        const payload = await updateResponse
          .json()
          .catch(() => ({ error: "Erreur mise à jour fond initial" }));
        const message = String(payload?.error || "Erreur mise à jour fond initial");
        const isLockedBySales =
          updateResponse.status === 409 ||
          message.toLowerCase().includes("session déjà utilisée") ||
          message.toLowerCase().includes("impossible de modifier le fond initial");

        if (isLockedBySales) {
          // Session déjà utilisée: on conserve la session courante sans bloquer la connexion.
          console.warn(
            "Session déjà utilisée, fond initial non modifiable:",
            session.id,
          );
          return session;
        }

        throw new Error(message);
      };

      const existingSession = await checkExistingSession(authenticatedUserId, authToken);

      if (existingSession) {
        const closedAmount = await closeExistingSessionBeforeNewOne(
          existingSession,
          authToken,
        );
        if (Number.isFinite(closedAmount)) {
          localStorage.removeItem("moo_cash_session");
        } else {
          throw new Error("Impossible de fermer l'ancienne session de caisse");
        }
      }
      const response = await apiFetch(`${API_BASE}/cash-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...buildAuthHeaders(authToken),
        },
        includeBearer: !authToken,
        tokenMode: forceSuperadmin ? "superadmin" : "admin",
        body: JSON.stringify({
          cashierId: authenticatedUserId,
          initialAmount: safeInitialAmount,
          notes
        }),
      });

      if (response.ok) {
        const session = await response.json();
        console.log('Session de caisse créée:', session);

        // Stocker les informations de session
        localStorage.setItem('moo_cash_session', JSON.stringify(session));

        // Procéder à la connexion
        onLogin(authenticatedUser, authenticatedToken || localStorage.getItem("moo_token"));
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 401) {
          const authenticationStillValid = await isAuthenticationStillValid(authToken);
          if (!authenticationStillValid) {
            resetExpiredAuthentication(authToken);
            throw createAuthenticationExpiredError();
          }
          throw new Error(
            errorData?.error ||
              "La session de caisse a refusé l'opération. Réessayez sans vous reconnecter.",
          );
        } else if (response.status === 409) {
          // Conflit : session déjà ouverte
          console.log('Session déjà ouverte, récupération de la session active');
          const existing = await checkExistingSession(authenticatedUserId, authToken);
          if (existing) {
            const syncedSession = await syncSessionInitialAmount(existing);
            localStorage.setItem('moo_cash_session', JSON.stringify(syncedSession || existing));
          }
          onLogin(authenticatedUser, authenticatedToken || localStorage.getItem("moo_token"));
        } else {
          const backendMessage =
            errorData?.error ||
            errorData?.message ||
            `Erreur création session (${response.status})`;
          throw new Error(backendMessage);
        }
      }
    } catch (error) {
      console.error('Erreur création session de caisse:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Gérer la confirmation du modal
  const handleCashDrawerConfirm = async (initialAmount, notes) => {
    try {
      await createCashSession(initialAmount, notes);
      setShowCashDrawer(false);
    } catch (error) {
      throw error; // Relancer l'erreur pour l'affichage dans le modal
    }
  };

  // Gérer les erreurs du modal
  const handleCashDrawerError = (error) => {
    console.error('Erreur modal fond de caisse:', error);
    // Optionnel : afficher une notification toast
  };

  return (
    <>
      {/* Composant Login standard */}
      <Login
        onLogin={handlePinLogin}
        forceSuperadmin={forceSuperadmin}
        requiredRole={requiredRole}
      />

      {/* Modal de fond de caisse pour les caissiers */}
      <CashDrawerModal
        isOpen={showCashDrawer}
        user={authenticatedUser}
        lastAmount={lastAmount}
        onConfirm={handleCashDrawerConfirm}
        onError={handleCashDrawerError}
      />
    </>
  );
};

export default CashierLogin;
